import {
  ExpiryDateChangeFields,
  useExpiryDateChangeForm
} from "PFApp/components/expiry_date_modal/expiry_date_change_fields";
import {
  FIELD_NAME_KEY,
  FIELD_POST_UNTIL_CHANGE_REASON_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { TemplateKey } from "PFApp/constants/templates";
import { useTemplate } from "PFApp/hooks";
import { MultiActivityForm, RoleFormKey } from "PFApp/types/activity_form/activity_form_schema";
import { InputFocusProps } from "PFComponents/table/interactive/use_cell_input_focus";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { PostUntilChangeReason } from "PFTypes";
import { RefObject } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { SidePanelCellFormSection } from "../side_panel_cell_form_section";

type ExpiryDateCellProps = {
  value: string | null;
  roleFormKey: RoleFormKey;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: string;
  title?: string;
  inputFocusProps?: InputFocusProps;
  buttonRef?: RefObject<HTMLButtonElement>;
};

export const ExpiryDateCell = ({
  value,
  roleFormKey,
  onChange,
  disabled,
  error,
  title,
  inputFocusProps,
  buttonRef
}: ExpiryDateCellProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "manageRoles.expiryDateSidePanel" });
  const { t: tExpiryDate } = useTranslation("activities", {
    keyPrefix: "form.sections.basicInformation.expiryDate"
  });
  const { t: tBlueprints } = useTranslation("activities", { keyPrefix: "engagementBlueprint" });
  const { formatDate } = useDateFormatter();
  const { setValue } = useFormContext();
  const roleTemplate = useTemplate(TemplateKey.Role)!;

  const roleName = useWatch({ name: `${roleFormKey}.${FIELD_NAME_KEY}` });
  const postUntilChangeReason = useWatch<MultiActivityForm>({
    name: `${roleFormKey}.${FIELD_POST_UNTIL_CHANGE_REASON_KEY}`
  }) as PostUntilChangeReason | null;

  const {
    control,
    handleSubmit,
    resetForm,
    minDate,
    isReasonSelectorEnabled,
    isOtherReason,
    dropdownOptions,
    buildPostUntilData,
    shouldEnableSave
  } = useExpiryDateChangeForm({
    date: value || undefined,
    reason: postUntilChangeReason,
    template: roleTemplate
  });

  const applyChanges = handleSubmit((formData) => {
    const data = buildPostUntilData(formData);
    onChange(data.date);
    setValue(`${roleFormKey}.${FIELD_POST_UNTIL_CHANGE_REASON_KEY}`, data.reason ?? null, {
      shouldDirty: true
    });
  });

  return (
    <SidePanelCellFormSection
      label={
        [
          value && formatDate(value),
          postUntilChangeReason?.text && `${tExpiryDate("reasonToChange")}: ${postUntilChangeReason.text}`
        ].filter(Boolean) as string[]
      }
      sidePanelTitle={t("title")}
      disabled={disabled}
      error={error}
      editAriaLabel={tBlueprints("editExpiryDateInRole", {
        roleName: roleName || tBlueprints("emptyRoleName")
      })}
      cellAriaLabel={title}
      inputFocusProps={inputFocusProps}
      buttonRef={buttonRef}
      onOpen={resetForm}
      onApply={applyChanges}
      isApplyDisabled={!shouldEnableSave}
    >
      <ExpiryDateChangeFields
        control={control}
        reasonLabel={tExpiryDate("modal.reasonLabel")}
        minDate={minDate}
        isReasonSelectorEnabled={isReasonSelectorEnabled}
        isOtherReason={isOtherReason}
        dropdownOptions={dropdownOptions}
      />
    </SidePanelCellFormSection>
  );
};
